import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Injector, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ValueAccessor } from '../../value-accessor/value-accessor';
import { DsSelectOverlayConfiguration, SelectFilterFn, SelectListOption } from '@bmw-ds/components';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';

@Component({
    selector: 'density-dropdown',
    templateUrl: './density-dropdown.component.html',
    styleUrl: './density-dropdown.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DensityDropdownComponent),
            multi: true
        }
    ],
    standalone: false
})
export class DensityDropdownComponent extends ValueAccessor<SelectListOption | SelectListOption[] | string | string[] | null> implements OnChanges {
    @Input()
    inputId: string = '';

    @Input()
    placeholder: string = '';

    @Input()
    multiSelect: boolean = false;

    @Input()
    variant: 'stacked' | 'inline' = 'inline';

    @Input()
    isFilterable: boolean = true;

    @Input()
    isInvalid: boolean = false;

    @Input()
    label: string = '';

    @Input()
    isDisabled: boolean = false;

    @Input()
    showClear: boolean = true;

    @Input()
    showOptionalLabel: boolean = false;

    @Input()
    isLoading: boolean = false;

    @Input()
    readOnly: boolean = false;

    @Input()
    styleClass: string = '';

    @Input()
    options: SelectListOption[] = [];

    @Input()
    noopScroll: boolean = false;

    @Input()
    sortSelectedToTop: boolean = true;

    @Input()
    customFilterFunction: SelectFilterFn | undefined;

    overlayConfig: Partial<DsSelectOverlayConfiguration> = {};

    @Output() selectionChanged = new EventEmitter<any>();

    constructor(private readonly scrollStrategyOptions: ScrollStrategyOptions, injector: Injector) {
        super(injector);
        this.updateScrollStrategy();
    }

    override writeValue(value: any): void {
        if (value !== null && value !== undefined && typeof value !== 'string' && !Array.isArray(value)) {
            value = String(value);
        }
        super.writeValue(value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['noopScroll']) {
            this.updateScrollStrategy();
        }
    }

    get filterFunction(): SelectFilterFn | undefined {
        if (this.customFilterFunction) {
            return this.customFilterFunction;
        }
        return this.multiSelect && this.sortSelectedToTop ? this.selectedFirstFilterFn : undefined;
    }

    get tooltipText(): string {
        if (this.value === null || this.value === undefined || this.value === '') {
            return '';
        }
        const values = Array.isArray(this.value) ? this.value : [this.value];
        return values
            .map(item => this.resolveOptionLabel(item))
            .filter(label => !!label)
            .join(', ');
    }

    private resolveOptionLabel(item: SelectListOption | string): string {
        if (item && typeof item === 'object') {
            return item.label ?? '';
        }
        const match = (this.options ?? []).find(option => option.id === item);
        return match ? match.label : String(item);
    }

    private readonly selectedFirstFilterFn: SelectFilterFn = (filterQuery: string, options: SelectListOption[]) => {
        const selectedIds = this.getSelectedIds();
        const query = filterQuery.toLowerCase();
        return options
            .filter(option => option.label.toString().toLowerCase().includes(query))
            .sort((first, second) => Number(selectedIds.includes(second.id)) - Number(selectedIds.includes(first.id)));
    };

    private getSelectedIds(): string[] {
        if (!this.value) {
            return [];
        }
        const values = Array.isArray(this.value) ? this.value : [this.value];
        return values.map(item => (typeof item === 'string' ? item : item?.id));
    }

    private updateScrollStrategy(): void {
        this.overlayConfig = {
            scrollStrategy: this.noopScroll ? this.scrollStrategyOptions.noop() : this.scrollStrategyOptions.block()
        };
    }
}
